<template>
  <!-- Loader -->
  <div
    v-if="showLoader"
    class="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50"
  >
    <svg
      class="animate-spin h-16 w-16 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="4"
        fill="none"
      />
    </svg>
  </div>

  <div v-else>
    <div class="relative overflow-x-auto shadow-md sm:rounded-sm mt-5">
      <table class="w-full text-left rtl:text-right text-gray-700">
        <thead>
          <tr class="bg-gray-100">
            <th scope="col" class="px-6 py-4">
              <div class="flex items-center">No.</div>
            </th>
            <th scope="col" class="px-6 py-4">
              <div class="flex items-center">Department Name</div>
            </th>
            <th scope="col" class="px-6 py-4">
              <div class="flex items-center">Total Services</div>
            </th>
            <th scope="col" class="px-6 py-4">
              <div class="flex items-center">Department Code</div>
            </th>
            <th scope="col" class="px-6 py-4">
              <div class="flex items-center">Action</div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(department, index) in departments"
            :key="department.id"
            class="border-b hover:bg-gray-50"
          >
            <td class="px-6 py-3">{{ index + 1 }}</td>
            <td class="px-6 py-3 font-medium">{{ department.name }}</td>
            <td class="px-6 py-3">
              <span
                class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
              >
                {{ department.service_count || 0 }} services
              </span>
            </td>
            <td class="px-6 py-3">
              <span class="text-gray-600 text-sm">{{ department.code }}</span>
            </td>
            <td class="px-6 py-3">
              <button
                @click="viewDepartment(department)"
                class="bg-custom-yellow text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-yellow-600 transition-colors"
              >
                View Department
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty State -->
      <div
        v-if="!showLoader && departments.length === 0"
        class="text-center py-12"
      >
        <svg
          class="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">No departments found</h3>
        <p class="mt-1 text-sm text-gray-500">
          Get started by adding service types to your facility.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";

export default {
  name: "FacilityServicesTab",
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      departments: [],
      showLoader: false,
    };
  },
  mounted() {
    this.fetchDepartments();
  },
  watch: {
    searchQuery(newQuery) {
      if (!newQuery.trim()) {
        this.fetchDepartments();
      }
    },
  },
  methods: {
    async fetchDepartments() {
      try {
        this.showLoader = true;

        // First, fetch all service types (departments)
        const serviceTypesResponse = await apiClient.getFacilityServiceTypes();

        if (serviceTypesResponse.success) {
          this.departments = serviceTypesResponse.data;

          // Fetch count of services for each department
          await this.fetchServiceCounts();
        } else {
          this.$toast.error("Failed to fetch departments");
        }
      } catch (error) {
        this.$toast.error("Failed to fetch departments");
        console.error(error);
      } finally {
        this.showLoader = false;
      }
    },

    async fetchServiceCounts() {
      try {
        // Fetch all services to count by department
        const servicesResponse = await apiClient.getFacilityServices({
          page: 1,
          per_page: 1000, // Get all to count
        });

        if (servicesResponse.success) {
          const services = servicesResponse.data;

          // Count services by department
          this.departments = this.departments.map((dept) => ({
            ...dept,
            service_count: services.filter(
              (service) => service.service_type.id === dept.id
            ).length,
          }));
        }
      } catch (error) {
        console.error("Failed to fetch service counts", error);
        // Continue without counts
      }
    },

    async searchFacilityServices(params) {
      if (!params.name || !params.name.trim()) {
        this.fetchDepartments();
        return;
      }

      try {
        this.showLoader = true;

        // Search departments by name
        const filtered = this.departments.filter((dept) =>
          dept.name.toLowerCase().includes(params.name.toLowerCase())
        );

        this.departments = filtered;
      } catch (error) {
        this.$toast.error("Unable to search departments");
      } finally {
        this.showLoader = false;
      }
    },

    viewDepartment(department) {
      const isLab = department.code === 'FST-002' || department.name?.toLowerCase().includes('lab')
      if (isLab) {
        this.$router.push({
          name: 'LabDisciplines',
          params: { departmentId: department.id, departmentName: department.name }
        })
      } else {
        this.$router.push({
          name:   'DepartmentServices',
          params: { departmentId: department.id, departmentName: department.name }
        })
      }
    },
  },
};
</script>

<style scoped>
.bg-custom-yellow {
  background-color: #f59e0b;
}
</style>